/* eslint-disable no-nested-ternary */
import { SagaIterator } from '@redux-saga/core';
import { call, put } from 'redux-saga/effects';
import { genFetchedData } from '../../../redux/fetchedData';
import uploadImage, {
  isTUploadErr,
  TResult,
  TUploadErr,
  TUploadFile,
} from '../../../utils/upload';
import updateToken from '../../../utils/updateToken';
import {
  setBusiness,
  setTempFile,
  setTempFileZip,
  setTempHTML,
} from '../reducer';
import { TRFileZip } from '../types';
import {
  fetchBusinessSphere,
  fetchImageRequest,
  fetchZipRequest,
} from '../model/actions';
import {
  getBusinessSphere,
  getUploadFileUrl,
  buildTemplate,
} from '../model/api';
import { makeReqWithRD, TMakeReqWithRD } from '../../../redux/makeReqWithRD';

export function* setFileSagaWorker({
  payload,
}: ReturnType<typeof fetchImageRequest>): SagaIterator<void> {
  const {
    typeSection,
    file,
    key,
    fileLimits: { maxSizeFile, minResolution, maxResolution },
  } = payload;
  let fetchedData = genFetchedData<TUploadFile | TUploadFile>(null).set(
    'isLoading',
    true,
  );
  yield put(setTempFile({ key, file: fetchedData }));
  try {
    yield call(updateToken);
    let requestUpload = '';
    requestUpload = yield call(getUploadFileUrl, { type: typeSection });

    const formData = new FormData();
    if (typeof file !== 'string') {
      if (file.size > maxSizeFile * 1024 * 1024) {
        const er = { code: 200032, message: '' };
        throw er;
      }
    }
    let keyFile = 'file';
    if (typeSection === 'client' || typeSection === 'project') {
      keyFile = 'logo';
    }
    if (typeSection === 'project' && key === 'offer') {
      keyFile = 'offer';
    }
    if (typeSection === 'project' && key === 'offer_en') {
      keyFile = 'offer_en';
    }
    if (key === 'iconMask' || key === 'images.icon') {
      keyFile = 'icon';
    }
    if (key === 'imgMask') {
      keyFile = 'image';
    }
    if (key === 'images.image_1' || key === 'images.image_2') {
      keyFile = key.slice(7);
    }

    formData.set(keyFile, file);

    const data:
      | TResult<Record<string, TUploadFile>>
      | TResult<TUploadFile>
      | TUploadErr = yield call(uploadImage, requestUpload, formData);
    const error = isTUploadErr(data) ? data.error : undefined;
    const result = isTUploadErr(data)
      ? undefined
      : Object.values(data.result)[0];

    if (error !== undefined) {
      throw error;
    }
    
/*     if (!file_ext.some((ext) => ext === result?.imageType)) {
      const er = { code: 20340, message: '' };
      throw er;
    } */

    if (
      result?.imageType === 'png' ||
      result?.imageType === 'jpg' ||
      result?.imageType === 'jpeg' ||
      result?.imageType === 'gif'
    ) {
      if (
        (result?.height ?? 0) < minResolution ||
        (result?.height ?? 0) > maxResolution ||
        (result?.width ?? 0) < minResolution ||
        (result?.width ?? 0) > maxResolution
      ) {
        const er = { code: 2031, message: '' };
        throw er;
      }
    }

    if (result && typeSection === 'templateImage') {
      const params = {
        template_id: 1,
        replacement_fields: {
          [keyFile]: { ...result },
        },
      };
      const htmlCode = yield call(buildTemplate, params);
      yield put(setTempHTML(htmlCode));
    }

    if (Number(result?.size ?? 0) > maxSizeFile * 1024) {
      const er = { code: 200032, message: '' };
      throw er;
    }

    if (result !== undefined) {
      fetchedData = fetchedData.set('data', result);
    }
  } catch (e) {
    console.error({ e });
    const code = e?.code ?? 0;
    fetchedData = fetchedData.set('error', {
      isError: true,
      message: e.message,
      code,
    });
    yield put(setTempFile({ key, file: fetchedData }));
  } finally {
    fetchedData = fetchedData.set('isLoading', false).set('LTU', Date.now());
    yield put(setTempFile({ key, file: fetchedData }));
  }
}

export function* setFileZipWorker({
  payload,
}: ReturnType<typeof fetchZipRequest>): SagaIterator<void> {
  const {
    file,
    key,
    fileLimits: { maxSizeFile },
    type,
  } = payload;
  let fetchedData = genFetchedData<TRFileZip>(null).set('isLoading', true);
  yield put(setTempFileZip({ key, file: fetchedData }));
  try {
    if (file.size > maxSizeFile * 1024 * 1024) {
      const er = { code: 200032, message: '' };
      throw er;
    }

    const requestUpload = yield call(getUploadFileUrl, { type });

    const formData = new FormData();
    formData.set('zip', file);

    const data: TResult<TRFileZip> | TUploadErr = yield call(
      uploadImage,
      requestUpload,
      formData,
    );
    const error = isTUploadErr(data) ? data.error : undefined;
    const result = isTUploadErr(data) ? undefined : data.result;

    if (error !== undefined) {
      throw error;
    }

    fetchedData = fetchedData.set('data', result as TRFileZip);
  } catch (e) {
    console.error({ e });
    const code = e?.code ?? 0;
    fetchedData = fetchedData.set('error', {
      isError: true,
      message: e.message,
      code,
    });
    yield put(setTempFileZip({ key, file: fetchedData }));
  } finally {
    fetchedData = fetchedData.delete('isLoading').set('LTU', Date.now());
    yield put(setTempFileZip({ key, file: fetchedData }));
  }
}

export function* fetchBusinessSphereWorker({
  payload,
}: ReturnType<typeof fetchBusinessSphere>): SagaIterator<void> {
  try {
    yield call<TMakeReqWithRD<typeof getBusinessSphere>>(makeReqWithRD, {
      fetcher: getBusinessSphere,
      fill: setBusiness,
      parameters: payload,
    });
  } catch (e) {
    console.error(e);
  }
}
